import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { EDSDataGrid } from '@gehc-eds-react/eds-datagrid';
import { EDSSearch } from '@gehc-eds-react/eds-search';
import { EDSButton } from '@gehc-eds-react/eds-button';
import { EDSBadge } from '@gehc-eds-react/eds-badge';
import { EDSIcon } from '@gehc-eds-react/eds-icon';
import { EDSPagination } from '@gehc-eds-react/eds-pagination';
import CreateDataUpload from './CreateDataUpload';
// import DataSummaryCard from '../../components/DataSummaryCard/DataSummaryCard';
import DataUploadChild from './DataUploadChild';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { addErrorNotification } from '../../redux/slices/notificationSlice';
import { fetchUploadedFiles, abortUploadAPI, completeDatasetAPI } from '../../redux/slices/uploadSlice';
import { formatFileSize, formatDuration, formatFileSizeFromMB  } from '../../utils/formatUtils';
import UploadSummaryModal from '../../components/UploadSummaryModal/UploadSummaryModal';
import ContractsList from '../../components/ContractsList/ContractsList';
import { selectDataProviderID, selectUserInitialized, selectUserError } from '../../redux/slices/userSlice';
import { fetchDatasetById } from '../../redux/slices/datasetSlice';
import { useSelector } from 'react-redux';
import { fetchContractsByDataProvider } from '../../services/contractsService';
import { selectSelectedDataProviderId, selectSelectedDataProviderDisplayName } from '../../redux/slices/selectedDataProviderSlice';
import SpinnerComponent from './../../components/SpinnerComponent/SpinnerComponent';
import { EDSBreadcrumb } from '@eds2-beta1-react/eds-breadcrumb';
import store from '../../redux/store';
import { 
    cancelUploadSession, 
    retryFailedFiles, 
    removeUploadSession,
    clearCompletedUploadSummary,
    setCompletedUploadSummary
} from '../../redux/slices/uploadProgressSlice';
import { 
    abortUpload, 
    getUploadFileObjects, 
    removeUploadFileObjects, 
    getUploadSessionData, 
    removeUploadSessionData,
    getUploadContext,
    removeUploadContext
} from '../../services/uploadAbortControllers';
import { startBackgroundUpload } from '../../services/uploadOrchestrator';
import logger from '../../utils/logger';
import { 
    getStatusBadgeVariant,
    getStatusDisplayName,
    UPLOAD_STATUS,
    DATASET_STATUS
} from '../../constants/uploadStatus';
import { 
    selectIsUploadEnabled, 
    selectFileExtensionsError, 
    selectFileExtensionsLoading,
    fetchFileExtensions 
} from '../../redux/slices/fileExtensionsSlice';
import { DEFAULT_PAGE_LIMIT } from '../../constants/apiEndpoints';

const DataUpload = () => {
    const dispatch = useAppDispatch();
    const { uploadedFiles, isLoading } = useAppSelector((state) => state.upload);
    const { activeUploads } = useAppSelector((state) => state.uploadProgress);
    const isUserInitialized = useAppSelector(selectUserInitialized);
    const isAnyApiLoading = isLoading || !isUserInitialized;
    const [isPaginationLoading, setIsPaginationLoading] = useState(false);

    // File extensions state
    const isUploadEnabled = useAppSelector(selectIsUploadEnabled);
    const fileExtensionsError = useAppSelector(selectFileExtensionsError);
    const fileExtensionsLoading = useAppSelector(selectFileExtensionsLoading);

    // Get completed upload summary for modal
    const completedUploadSummary = useAppSelector((state) => state.uploadProgress.completedUploadSummary);
 
    const [isCreateDataUpload, setIsCreateDataUpload] = useState(false);
    const [editingData, setEditingData] = useState(null);
    const [selectedDataset, setSelectedDataset] = useState(null);
    const [isAddingData, setIsAddingData] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);

    const selectedDataProviderId = useSelector(selectSelectedDataProviderId);
    const selectedDataProviderDisplayName = useSelector(selectSelectedDataProviderDisplayName);
    // Contract state
    const [contracts, setContracts] = useState([]);
    const [selectedContract, setSelectedContract] = useState(null);
    const [isContractsLoading, setIsContractsLoading] = useState(false);

    const [currentView, setCurrentView] = useState('contracts');
    const [contractsPagination, setContractsPagination] = useState({
        currentPage: 1,
        pageSize: DEFAULT_PAGE_LIMIT,
        hasNextPage: false
    });

    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: DEFAULT_PAGE_LIMIT,
        hasNextPage: false,
        total: 0
    });

    //Contract Search State
    const contractSearchTimeoutRef = useRef(null);
    const [contractSearchTerm, setContractSearchTerm] = useState('');
    const searchTimeoutRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    // Refs to prevent duplicate API calls
    const hasCalledFetchUploadedFiles = useRef(false);
    const shownUploadErrors = useRef(new Set());

    const [dataList, setDataList] = useState([]);
    const [isMounting, setIsMounting] = useState(true);
      // Show notification if file extensions API failed
    useEffect(() => {
        if (fileExtensionsError && !fileExtensionsLoading) {
            dispatch(addErrorNotification({
                message: 'Unable to load supported file types. Upload is temporarily unavailable. Please refresh the page to try again.'
            }));
        }
    }, [fileExtensionsError, fileExtensionsLoading, dispatch]);

    const fetchDataPage = useCallback(async (page, search = '') => {
        setIsPaginationLoading(true);
        const offset = (page - 1) * pagination.pageSize;

        // Guard: Don't fetch if no contract is selected in datasets view
        if (currentView === 'datasets' && !selectedContract?.ContractID) {
            setIsPaginationLoading(false);
            return;
        }


        try {
            const requestPayload = { 
                limit: pagination.pageSize, 
                offset,
                contractId: selectedContract?.ContractID,
                search: search.trim()
            };

            const response = await dispatch(fetchUploadedFiles(requestPayload)).unwrap();
            const datasets = Array.isArray(response) ? response : (response?.Datasets || []);
            const total = response?.total || 0;

            setPagination(prev => ({
                ...prev,
                currentPage: page,
                hasNextPage: datasets.length === pagination.pageSize,
                total: total
            }));
            //Inside fetchDataPage, after API response is received.
            //ensures searchTerm only updates AFTER fresh data arrives, preventing UI flicker from stale search term during loading.
           //searchTerm tracks the search term that was successfully sent to the API and whose results are currently displayed
            setSearchTerm(search.trim()); 

        } catch (error) {
            console.error('Failed to fetch page:', error);
        } finally {
            setIsPaginationLoading(false);
            
        }
    }, [dispatch, pagination.pageSize, selectedContract, currentView]);

   const fetchContractsPage = useCallback(async (page, search = '') => {
        if (!selectedDataProviderId) return;
        
        setIsContractsLoading(true);
        
        const offset = (page - 1) * contractsPagination.pageSize;
        
        try {
            const result = await fetchContractsByDataProvider(
                selectedDataProviderId,
                contractsPagination.pageSize,
                offset,
                search.trim()
            );
            
            setContracts(
                (result.contracts || []).map(contract => ({
                    ...contract,
                    StartDate: contract.EffectiveDate,
                    EndDate: contract.ExpirationDate
                }))
            );
            setContractsPagination(prev => ({
                ...prev,
                currentPage: page,
                hasNextPage: (offset + contractsPagination.pageSize) < (result.total || 0),
                total: result.total || 0
            }));
            
        } catch (err) {
            console.error('Failed to fetch contracts:', err);
            setContracts([]);
        } finally {
            setIsContractsLoading(false);
        }
    }, [selectedDataProviderId, contractsPagination.pageSize]);

     useEffect(() => {
        if (selectedDataProviderId) {
            setIsContractsLoading(true);
            setContracts([]);
            setSelectedContract(null);
            setCurrentView('contracts');
            
            setContractSearchTerm('');
            if (contractSearchTimeoutRef.current) {
                clearTimeout(contractSearchTimeoutRef.current);
            }
            
            fetchContractsPage(1, '').then(() => {
                // Success callback if needed
            }).catch((err) => {
                console.error('Failed to fetch contracts:', err);
                setContracts([]);
            }).finally(() => {
                setIsContractsLoading(false);
            });
        } else {
            setContracts([]);
            setSelectedContract(null);
            setCurrentView('contracts');
            setContractSearchTerm(''); 
        }
    }, [selectedDataProviderId, fetchContractsPage]);


     const handleContractSelect = (contract) => {
        // ✅ Clear old data FIRST
        setDataList([]);
        setPagination({
            currentPage: 1,
            pageSize: DEFAULT_PAGE_LIMIT,
            hasNextPage: false,
            total: 0
        });
        setSearchTerm('');

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        // ✅ Reset initialization so merge effect doesn't run with stale data
        setHasInitialized(false);
        hasCalledFetchUploadedFiles.current = false;
        
        // Then set new contract and switch view
        setSelectedContract(contract);
        setCurrentView('datasets');
    };

    useEffect(() => {
        if (selectedContract && currentView === 'datasets') {
            setHasInitialized(true);
            fetchDataPage(1);
        }
    }, [selectedContract]);
   //filteredDataList will still filter by old search text after switching contracts.so we can use searchTerm to reset the search when switching contracts. This way, the dataList will be fully shown for the new contract until the user types in the search box again.
    const handleBackToContracts = () => {
        setSelectedContract(null);
        setCurrentView('contracts');
        setDataList([]);
        // ✅ ADD: Clear search term when going back
        setSearchTerm('');
        // ✅ ADD: Clear any pending search timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
    };
// useCallback takes a snapshot of everything inside it when it is created.
// If fetchDataPage is not in the dependency array, handleSearchClear never gets the updated version of fetchDataPage. It keeps using the old snapshot.
    const handleContractSearchInput = useCallback((e) => {
        const newSearchTerm = e.detail?.searchTerm ?? '';
        setContractSearchTerm(newSearchTerm);
        
        if (contractSearchTimeoutRef.current) {
            clearTimeout(contractSearchTimeoutRef.current);
        }
        
        contractSearchTimeoutRef.current = setTimeout(() => {
            if (selectedDataProviderId) {
                fetchContractsPage(1, newSearchTerm);
            }
        }, 2000); 
    }, [selectedDataProviderId, fetchContractsPage]);

    const handleContractSearchClear = useCallback(() => {
        setContractSearchTerm('');
        
        if (contractSearchTimeoutRef.current) {
            clearTimeout(contractSearchTimeoutRef.current);
        }

        if (selectedDataProviderId) {
            fetchContractsPage(1, '');
        }
    }, [selectedDataProviderId, fetchContractsPage]);


    useEffect(() => {
        return () => {
            if (contractSearchTimeoutRef.current) {
                clearTimeout(contractSearchTimeoutRef.current);
            }
        };
    }, []);
   
   
    // Add these search handlers
    const handleSearchInput = useCallback((e) => {
        const newSearchTerm = e.detail?.searchTerm ?? '';

    
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
    
        if (!newSearchTerm.trim()) {
            if (selectedContract?.ContractID) {
                fetchDataPage(1, '');  
            }
        return;
    }
    
        searchTimeoutRef.current = setTimeout(() => {
            if (selectedContract?.ContractID) {
                fetchDataPage(1, newSearchTerm);
            }
        }, 2000); 
    }, [fetchDataPage, selectedContract]);

   const handleSearchClear = useCallback(() => {
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (selectedContract?.ContractID) {
            fetchDataPage(1, '');
        } 
    }, [selectedContract,fetchDataPage]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isAnyApiLoading && isMounting) {
            setIsMounting(false);
        }
    }, [isAnyApiLoading, isMounting]);

    useLayoutEffect(() => {
        setDataList([]);
        // setHasInitialized(true);
    }, []);

    // Pagination handlers
    const handlePreviousPage = () => {
        if (pagination.currentPage > 1) {
            fetchDataPage(pagination.currentPage - 1, searchTerm);
        }
    };

    const handleNextPage = () => {
        if (pagination.hasNextPage) {
            fetchDataPage(pagination.currentPage + 1, searchTerm);
        }
    };

    const handleDatasetsPageNumberClick = (e) => {
        const newPage = e.detail?.currentPage || e.detail?.value || e.detail;
        if (newPage && Number(newPage) !== pagination.currentPage) {
            fetchDataPage(Number(newPage), searchTerm);
        }
    };

     // ✅ Contracts pagination handlers
    const handleContractsPreviousPage = () => {
        if (contractsPagination.currentPage > 1) {
            fetchContractsPage(contractsPagination.currentPage - 1, contractSearchTerm);
        }
    };

    const handleContractsNextPage = () => {
        if (contractsPagination.hasNextPage) {
            fetchContractsPage(contractsPagination.currentPage + 1, contractSearchTerm);
        }
    };

    // Contracts page number click handler
    const handleContractsPageNumberClick = (e) => {
        const newPage = e.detail?.currentPage || e.detail?.value || e.detail;
        if (newPage && Number(newPage) !== contractsPagination.currentPage) {
            fetchContractsPage(Number(newPage), contractSearchTerm);
        }
    };

    useEffect(() => {
        // Don't merge data until component is properly initialized
        if (!hasInitialized) return;
        if (currentView !== 'datasets') return;
        if (!selectedContract?.ContractID) return;

        // ✅ Skip merge if uploadedFiles is stale (from previous contract)
        if (uploadedFiles.length > 0 && uploadedFiles[0]?.ContractID && 
            uploadedFiles[0].ContractID !== selectedContract.ContractID) {
            setDataList([]);
            return;
        }

        // ✅ Filter active uploads to only show ones for THIS contract
        const contractActiveUploads = activeUploads.filter(upload => {
            const uploadContractId = upload.contract?.ContractID || upload.contract?.value;
            return uploadContractId === selectedContract.ContractID;
        });

        // Transform active uploads (only for this contract)
       const activeUploadsList = contractActiveUploads.map((upload) => {
    const totalSize = upload.files.reduce((sum, file) => sum + file.size, 0);

            // Count files that have actually started uploading (or finished/failed)
            const startedFilesCount = upload.files.filter((f) =>
                (f.uploadedBytes || 0) > 0 ||
                f.status === UPLOAD_STATUS.COMPLETED ||
                f.status === UPLOAD_STATUS.FAILED ||
                f.status === UPLOAD_STATUS.PARTIALLY_FAILED ||
                f.status === UPLOAD_STATUS.CANCELLED
            ).length;

            return {
                uid: `active_${upload.uploadId}`,
                name: upload.datasetName,
                filesCount: upload.files.length,
                dataSize: formatFileSize(totalSize),
                date: new Date(upload.startedAt).toLocaleDateString(),
                status: upload.status,
                progress: upload.overallProgress,
                uploadId: upload.uploadId,
                isActive: true,

                // This will now move like 0/91 -> 1/91 -> 2/91...
                filesStatus: `${startedFilesCount}/${upload.files.length}`,
                amsInfectedFilesCount: '—',
                amsFailedFilesCount: '—',
                addedBy: '—',
                files: upload.files,
                overallProgress: upload.overallProgress,
                originalData: upload
          };
        });

        // Transform uploaded files
        const completedUploadsList = uploadedFiles.map((dataset) => ({
            uid: dataset.DatasetID,
            name: dataset.DatasetDisplayName || dataset.DatasetName,
            filesCount: dataset.TotalFilesCount || 0,
            dataSize: formatFileSizeFromMB(dataset.TotalFilesSize || 0),
            date: new Date(dataset.AddedAt).toLocaleDateString(),
            status: dataset.Status,
            isActive: false,
            amsInfectedFilesCount: dataset.AMSInfectedFilesCount ?? 0,
            amsFailedFilesCount: dataset.AMSFailedFilesCount ?? 0,
            addedBy: dataset.AddedBy || '—',
            originalData: dataset
        }));

        // Deduplicate
        const activeDatasetIds = contractActiveUploads
            .map(upload => upload.datasetId)
            .filter(Boolean);
        
        const deduplicatedCompletedList = completedUploadsList.filter(
            dataset => !activeDatasetIds.includes(dataset.uid)
        );

        // Merge: active uploads at top (only on page 1), then completed uploads
        let mergedList;
        if (pagination.currentPage === 1) {
            mergedList = [...activeUploadsList, ...deduplicatedCompletedList];
        } else {
            mergedList = deduplicatedCompletedList;
        }
        
        setDataList(mergedList);
    }, [hasInitialized, activeUploads, uploadedFiles, pagination.currentPage, currentView, selectedContract]);
     /***
      * dataList contains both API results (server-filtered) AND active uploads from Redux 
      * (not server-filtered, since they're local in-progress items). This filter hides any active 
      * upload whose name doesn't match the current search term.
      */
    const filteredDataList = useMemo(() => {
    if (!searchTerm?.trim()) return dataList;

    const lowerSearch = searchTerm.toLowerCase();

    return dataList.filter(item => {
        const itemName = (item.fileName || item.name || item.DatasetName || '').toLowerCase();
        return itemName.includes(lowerSearch);
    });
}, [dataList, searchTerm]);

    // Show error notifications for failed uploads
    useEffect(() => {
        const uploadErrors = activeUploads.filter(upload => upload.error);
        
        uploadErrors.forEach(upload => {
            const errorKey = `${upload.uploadId}-${upload.error}`;
            if (!shownUploadErrors.current.has(errorKey)) {
                shownUploadErrors.current.add(errorKey);
                dispatch(addErrorNotification({
                    message: upload.error
                }));
            }
        });
    }, [activeUploads, dispatch]);

    // const summaryData = [
    //     {
    //         title: "Total data size",
    //         value: "150GB",
    //         progress: 75,
    //         color: "#808080"
    //     },
    //     {
    //         title: "Successful",
    //         value: "120GB",
    //         progress: 75,
    //         color: "#4CAF50"
    //     },
    //     {
    //         title: "In-progress",
    //         value: "5",
    //         progress: 10,
    //         color: "#A57C00"
    //     },
    //     {
    //         title: "Partially failed",
    //         value: "2GB",
    //         progress: 25,
    //         color: "#FFC107"
    //     },
    //     {
    //         title: "Failed",
    //         value: "4GB",
    //         progress: 25,
    //         color: "#F44336"
    //     },
    //     {
    //         title: "Space availability",
    //         value: "15 TB",
    //         progress: 25,
    //         color: "#808080"
    //     }
    // ];

    const createUploadButtonClick = () => {
        if (!isUploadEnabled) {
            dispatch(addErrorNotification({
                message: 'Upload is temporarily unavailable. Supported file types could not be loaded. Please refresh the page to try again.'
            }));
            return;
        }
        setEditingData(null);
        setCurrentView('upload');
        setIsCreateDataUpload(true);
    };

    const handleCreateDataUpload = useCallback(async (newData) => {
        setEditingData(null);
    }, []);

    const handleCloseCreateUpload = useCallback(() => {
        setIsCreateDataUpload(false);
        setEditingData(null);
        setIsAddingData(false);
        setCurrentView('datasets');
    }, []);

    const handleUploadedDatasetClick = useCallback((row) => {
        logger.debug('Dataset clicked:', {
            name: row.name,
            uid: row.uid,
            isActive: row.isActive,
            originalData: row.originalData
        });
        
        if (row.isActive && row.originalData?.datasetId) {
            logger.debug('Active upload clicked - using datasetId:', row.originalData.datasetId);
            setSelectedDataset({
                ...row,
                uid: row.originalData.datasetId,
                originalData: {
                    ...row.originalData,
                    DatasetID: row.originalData.datasetId
                }
            });
        } else if (!row.isActive) {
            logger.debug('Completed dataset clicked - using uid:', row.uid);
            setSelectedDataset(row);
        } else {
            logger.warn('Active upload clicked but no datasetId found');
        }
    }, []);

    const handleCancelUpload = useCallback((uploadId) => {
        logger.debug(`handleCancelUpload (Pause) called for uploadId: ${uploadId}`);
        
        dispatch(cancelUploadSession({ uploadId }));
        logger.debug(`Upload ${uploadId} marked as PAUSED in Redux`);
        
        const aborted = abortUpload(uploadId);
        
        if (aborted) {
            logger.debug(`Upload ${uploadId} inflight requests canceled (pause)`);
        } else {
            logger.debug(`Upload ${uploadId} has no inflight requests to cancel`);
        }
    }, [dispatch]);

    const handleAbortUpload = useCallback(async (uploadId) => {
        logger.debug(`handleAbortUpload called for uploadId: ${uploadId}`);
        
        const sessionData = getUploadSessionData(uploadId);
        logger.debug(`Session data for abort (${sessionData?.length || 0} files):`, sessionData);
        
        if (!sessionData || sessionData.length === 0) {
            logger.debug(`No session data found - upload failed during initialization`);
            logger.debug(`Removing upload ${uploadId} from UI (no server cleanup needed)`);
            
            dispatch(removeUploadSession({ uploadId }));
            removeUploadFileObjects(uploadId);
            return;
        }
        
        logger.debug(`Session data found - proceeding with server abort`);
        
        const aborted = abortUpload(uploadId);
        
        if (aborted) {
            logger.debug(`Upload ${uploadId} API calls aborted`);
        } else {
            logger.debug(`Upload ${uploadId} is not currently running (already paused/failed)`);
        }
        
        const state = store.getState();
        const upload = state.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
        
        if (!upload?.datasetId) {
            console.warn(`No dataset_id found for upload ${uploadId} - cannot abort on server`);
            dispatch(removeUploadSession({ uploadId }));
            removeUploadFileObjects(uploadId);
            return;
        }
        
        try {
            await dispatch(abortUploadAPI({
                dataset_id: upload.datasetId,
                cancel_reason: 'User cancelled upload'
            })).unwrap();
            
            logger.debug(`Server notified of abort for dataset: ${upload.datasetId}`);
        } catch (error) {
            console.error(`Failed to notify server of abort:`, error);
            console.error(`Abort API failed - keeping upload in UI. User can retry abort or wait for connection.`);
            
            logger.debug(`Keeping upload ${uploadId} in UI for retry`);
            return;
        }
        
        try {
            const uploadContext = getUploadContext(uploadId);
            
            if (upload?.datasetId && uploadContext) {
                const datasetName = upload.datasetName || '';
                const contractName = upload.contract?.Name || uploadContext.contractName || '';
                
                logger.debug(`Marking dataset ${upload.datasetId} as UPLOAD_ABORTED`);
                await dispatch(completeDatasetAPI({
                    datasetId: upload.datasetId,
                    status: DATASET_STATUS.UPLOAD_ABORTED,
                    businessUnit: uploadContext.businessUnit,
                    project: uploadContext.project,
                    contractId: uploadContext.contractId,
                    dataProviderName: uploadContext.dataProviderName,
                    contractName: contractName,
                    datasetName: datasetName
                })).unwrap();
                logger.debug(`Dataset ${upload.datasetId} marked as UPLOAD_ABORTED`);
            } else {
                console.warn(`Cannot mark dataset as aborted - missing datasetId or uploadContext`);
            }
        } catch (error) {
            console.error(`Failed to mark dataset as aborted:`, error);
        }
        // ========== Build and dispatch abort summary BEFORE removing session ==========
        const summaryUpload = store.getState().uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
        if (summaryUpload) {

            const completedFiles = summaryUpload.files.filter(f => f.status === UPLOAD_STATUS.COMPLETED);
            const failedFilesArr = summaryUpload.files.filter(f => 
                f.status === UPLOAD_STATUS.FAILED || f.status === UPLOAD_STATUS.PARTIALLY_FAILED
            );
            const skippedFilesFromUpload = summaryUpload.skippedFilesCount || 0;
            // Count cancelled files separately — these are files that were aborted mid-upload
            // NOT the same as "skipped" files (which were rejected before upload started)
            const cancelledFiles = summaryUpload.files.filter(f => 
                f.status === UPLOAD_STATUS.CANCELLED || 
                f.status === UPLOAD_STATUS.PAUSED || 
                f.status === UPLOAD_STATUS.PENDING ||
                f.status === UPLOAD_STATUS.UPLOADING ||
                f.status === UPLOAD_STATUS.COMPUTING_CHECKSUMS
            );
            const totalSize = summaryUpload.files.reduce((sum, f) => sum + f.size, 0);
            const uploadedSize = summaryUpload.files.reduce((sum, f) => sum + (f.uploadedBytes || 0), 0);

            const endTime = new Date().toISOString();
            const startTime = summaryUpload.startedAt;
            const durationMs = new Date(endTime) - new Date(startTime);
            const durationSeconds = Math.round(durationMs / 1000);

            const summary = {
                uploadId: summaryUpload.uploadId,
                datasetName: summaryUpload.datasetName,
                status: DATASET_STATUS.UPLOAD_ABORTED, 
                totalFiles: summaryUpload.files.length + skippedFilesFromUpload,
                successfulFiles: completedFiles.length,
                failedFiles: failedFilesArr.length,
                skippedFiles: skippedFilesFromUpload,
                cancelledFiles: cancelledFiles.length,
                totalSize,
                uploadedSize,
                startedAt: startTime,
                completedAt: endTime,
                durationSeconds,
            };

            dispatch(setCompletedUploadSummary(summary));
            logger.debug(`📊 Abort summary dispatched for modal:`, summary);
        }
        
        logger.debug(`Removing upload ${uploadId} from Redux`);
        dispatch(removeUploadSession({ uploadId }));
        
        try {
            logger.debug(`Refreshing uploaded files grid`);
            await dispatch(fetchUploadedFiles({ 
                limit: DEFAULT_PAGE_LIMIT, 
                offset: 0,
                contractId: selectedContract?.ContractID
            }));
            logger.debug(`Grid refreshed after abort`);
        } catch (error) {
            console.error(`Failed to refresh grid:`, error);
        }
        
        removeUploadFileObjects(uploadId);
        removeUploadSessionData(uploadId);
        removeUploadContext(uploadId);
        
        logger.debug(`Upload ${uploadId} fully cleaned up after abort`);
    }, [dispatch, selectedContract]);

      const handleRetryUpload = useCallback((uploadId) => {
        logger.debug(`handleRetryUpload called for uploadId: ${uploadId}`);
        
        const uploadBeforeRetry = activeUploads.find(u => u.uploadId === uploadId);
        logger.debug(` Redux state BEFORE retry:`, {
            uploadId,
            status: uploadBeforeRetry?.status,
            overallProgress: uploadBeforeRetry?.overallProgress,
            files: uploadBeforeRetry?.files.map(f => ({
                id: f.id,
                name: f.name,
                size: f.size,
                uploadedBytes: f.uploadedBytes,
                progress: f.progress,
                status: f.status
            }))
        });
        
        logger.debug(` Dispatching retryFailedFiles for ${uploadId}`);
        dispatch(retryFailedFiles({ uploadId }));
        
        logger.debug(`Reading updated state from store...`);
        const state = store.getState();
        const upload = state.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
        logger.debug(` Upload found in Redux (from store):`, upload);
        
        logger.debug(` Retrieving stored file objects for ${uploadId}...`);
        const storedFiles = getUploadFileObjects(uploadId);
        logger.debug(` Stored files retrieved (${storedFiles?.length || 0} files):`, storedFiles);
        
        logger.debug(` Retrieving stored upload context for ${uploadId}...`);
        const storedUploadContext = getUploadContext(uploadId);
        logger.debug(` Stored upload context retrieved:`, storedUploadContext);
        
        if (upload && storedFiles && storedUploadContext) {
            const uploadContext = storedUploadContext;
            logger.debug(` Using stored upload context:`, uploadContext);
            
            const filesWithObjects = upload.files.map(file => {
                const storedFile = storedFiles.find(f => f.id === file.id);
                logger.debug(`   Mapping file "${file.name}":`, {
                    hasStoredFile: !!storedFile,
                    hasFileObject: !!storedFile?.file
                });
                return {
                    id: file.id,
                    name: file.name,
                    size: file.size,
                    relativePath: file.relativePath,
                    file: storedFile?.file
                };
            });
            logger.debug(` Files with objects prepared (${filesWithObjects.length} files):`, filesWithObjects);
            
            logger.debug(` Calling startBackgroundUpload with:`, {
                uploadId: upload.uploadId,
                datasetId: upload.datasetId,
                filesCount: filesWithObjects.length,
                uploadContext
            });
            
            try {
                startBackgroundUpload(
                    upload.uploadId,
                    upload.datasetId,
                    filesWithObjects,
                    uploadContext
                );
                logger.debug(`startBackgroundUpload called successfully for ${uploadId}`);
            } catch (error) {
                console.error(` Error calling startBackgroundUpload:`, error);
            }
            
            logger.debug(`Resuming upload: ${uploadId} with ${filesWithObjects.length} files`);
        } else {
            console.error(`Cannot retry upload ${uploadId}:`, {
                hasUpload: !!upload,
                hasStoredFiles: !!storedFiles,
                hasStoredUploadContext: !!storedUploadContext,
                uploadId: uploadId
            });
            
            if (!upload) {
                console.error('Upload not found in Redux store. Available uploads:', state.uploadProgress.activeUploads.map(u => u.uploadId));
                alert('Upload session not found. Please try aborting and re-uploading the files.');
            } else if (!storedFiles || !storedUploadContext) {
                console.error('File objects or upload context not found in storage. This happens after page reload.');
                alert(
                    '⚠️ Cannot retry upload after page reload.\n\n' +
                    'File objects and upload context are lost when the browser refreshes. Please:\n' +
                    '1. Abort this upload\n' +
                    '2. Start a new upload with the same files\n\n' +
                    'Tip: Keep the browser tab open to pause/resume uploads without losing progress.'
                );
                
                dispatch(cancelUploadSession({ uploadId }));
            }
        }
    }, [activeUploads, dispatch]);

    const handleRemoveUpload = useCallback((uploadId) => {
        dispatch(removeUploadSession({ uploadId }));
        removeUploadFileObjects(uploadId);
    }, [dispatch]);

    // Upload Summary Modal handlers and helpers
    const handleCloseSummaryModal = useCallback(() => {
        dispatch(clearCompletedUploadSummary());
    }, [dispatch]);

    const renderAbortButton = useCallback((uploadId) => (
        <span 
            className="action-icon"
            style={{ 
                cursor: 'pointer', 
                display: 'inline-flex', 
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                color: '#d32f2f',
                fontSize: '14px',
                padding: '6px 10px',
                borderRadius: '4px',
                backgroundColor: '#ffebee',
                border: '1px solid #ffcdd2',
                lineHeight: '1',
                minHeight: '28px'
            }}
            title="Abort upload (cannot be resumed)"
            onClick={() => handleAbortUpload(uploadId)}

        >
            <EDSIcon 
                iconName="ico-close-16" 
                style={{ verticalAlign: 'middle', '--eds-text-icon-default': 'var(--eds-status-text-icon-dark)' }}
            ></EDSIcon>
            <span style={{ verticalAlign: 'middle', marginBottom: '2px' }}>Abort</span>
        </span>
    ), [handleAbortUpload]);

    const renderInProgressButtons = useCallback((row) => {
        const hasAnyUploading = row.files && row.files.length > 0 &&
            row.files.some(f => f.status === UPLOAD_STATUS.UPLOADING);
        
        const isInitializing = !hasAnyUploading;
        
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span 
                    className="action-icon"
                    style={{ 
                        cursor: 'pointer', 
                        display: 'inline-flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        color: '#7252BC',
                        fontSize: '14px',
                        padding: '5px 12px',
                        borderRadius: '4px',
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #ddd',
                        lineHeight: '1',
                        minHeight: '28px'
                    }}
                    title="Pause upload"
                    onClick={() => handleCancelUpload(row.uploadId)}

                >
                <EDSIcon 
                    iconName="ico-media-pause-16" 
                    style={{ verticalAlign: 'middle', '--eds-text-icon-default': 'var(--eds-status-text-icon-dark)' }}
                ></EDSIcon>
                <span style={{ verticalAlign: 'middle' }}>Pause</span>
                </span>
                
                {isInitializing ? (
                    <span 
                        style={{ 
                            cursor: 'not-allowed', 
                            display: 'inline-flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            color: '#999',
                            opacity: 0.6,
                            fontSize: '14px',
                            padding: '6px 10px',
                            lineHeight: '1',
                            minHeight: '28px'
                        }}
                        title="Initializing upload..."
                    >
                        <span>Initializing...</span>
                    </span>
                ) : (
                    renderAbortButton(row.uploadId)
                )}
            </div>
        );
    }, [handleCancelUpload, renderAbortButton]);

    const dataColumn = useMemo(() => ({
        "uid": {"displayName": "Id", "isPrimary": true, "isHidden": true},
        "name": {
            "displayName": "Dataset name",
            renderCellValue: (name, row) => (
                <span
                    className="menu-link"
                    onClick={() => handleUploadedDatasetClick(row)}
                    style={{ 
                    cursor: "pointer", 
                    color: "#7252BC", 
                    textDecoration: "underline",
                    display: "inline-block",
                    maxWidth: "220px", // Adjust as needed
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "middle"
                }}
                title={name}
                >
                    {name}
                </span>
            )
        },
        "filesCount": {
            "displayName": "Files",
            renderCellValue: (value, row) => {
                if (row.isActive) {
                    return <span>{row.filesStatus || '—'}</span>;
                }
                return <span>{value || 0}</span>;
            }
        },
        "dataSize": {
            "displayName": "Size",
        },
        "amsInfectedFilesCount": {
            "displayName": "AMS Infected Files",
            renderCellValue: (value, row) => {
                if (row.isActive) return <span>—</span>;
                return (
                    <span style={{ color: (value > 0) ? '#d32f2f' : 'inherit'}}>
                        {value ?? 0}
                    </span>
                );
            }
        },
        "amsFailedFilesCount": {
            "displayName": "AMS Failed Files",
            renderCellValue: (value, row) => {
                if (row.isActive) return <span>—</span>;
                return (
                    <span style={{ color: (value > 0) ? '#d32f2f' : 'inherit' }}>
                        {value ?? 0}
                    </span>
                );
            }
        },
        "addedBy": {
            displayName: "Added By",
            renderCellValue: (value, row) => (
                <div
                    style={{
                        width: "100px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block"
                    }}
                    title={value}
                >
                    {value || '—'}
                </div>
            )
        },
        "date": {"displayName": "Date"},
        "progress": {
            "displayName": "Progress",
            renderCellValue: (progress, row) => {
                if (!row.isActive) return <div></div>;
                
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                        <div style={{ flex: 1, minWidth: '100px', position: 'relative', height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ 
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                height: '100%',
                                width: `${progress || 0}%`,
                                backgroundColor: '#7252BC',
                                borderRadius: '4px',
                                transition: 'width 0.3s ease'
                            }} />
                        </div>
                        <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {progress || 0}% ({row.filesStatus})
                        </span>
                    </div>
                );
            }
        },
        "status": {
            "displayName": "Status",
            renderCellValue: (status, row) => {
                return (
                    <EDSBadge
                        variant={getStatusBadgeVariant(status)}
                        value={getStatusDisplayName(status)} 
                        label={""}
                        size={"medium"}
                    />
                );
            }
        },
        "action": {
            "displayName": "Action",
            "width": 200,
            renderCellValue: (action, row) => {
                if (!row.isActive) return <div></div>;

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {row.status === 'in-progress' && renderInProgressButtons(row)}
                        
                        {row.status === 'paused' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span 
                                    className="action-icon"
                                    style={{ 
                                        cursor: 'pointer', 
                                        display: 'inline-flex', 
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                        color: '#2e7d32',
                                        fontSize: '14px',
                                        padding: '6px 10px',
                                        borderRadius: '4px',
                                        backgroundColor: '#e8f5e8',
                                        border: '1px solid #c8e6c9',
                                        lineHeight: '1',
                                        minHeight: '28px'
                                    }}
                                    title="Resume upload"
                                    onClick={() => handleRetryUpload(row.uploadId)}

                                >
                                <EDSIcon 
                                    iconName="ico-arrow-right-16" 
                                    style={{ verticalAlign: 'middle', '--eds-text-icon-default': 'var(--eds-status-text-icon-dark)' }}
                                ></EDSIcon>
                                <span style={{ verticalAlign: 'middle', marginBottom: '2px' }}>Resume</span>
                                </span>
                                {renderAbortButton(row.uploadId)}
                            </div>
                        )}
                        
                        {row.status === 'partially-failed' && (
                            <>
                                <span 
                                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                    title="Retry failed files"
                                    onClick={() => handleRetryUpload(row.uploadId)}
                                >
                                    <EDSIcon iconName="ico-reset-16"></EDSIcon>
                                    <span style={{ fontSize: '14px' }}>Retry</span>
                                </span>
                                {renderAbortButton(row.uploadId)}
                            </>
                        )}
                        
                        {row.status === 'failed' && (
                            <span 
                                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                title="Remove upload"
                                onClick={() => handleRemoveUpload(row.uploadId)}
                            >
                                <EDSIcon iconName="ico-close-24"></EDSIcon>
                                <span style={{ fontSize: '14px' }}>Remove</span>
                            </span>
                        )}
                        
                        {row.status === 'cancelled' && (
                            <span 
                                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                title="Remove upload"
                                onClick={() => handleRemoveUpload(row.uploadId)}
                            >
                                <EDSIcon iconName="ico-trash-16"></EDSIcon>
                                <span style={{ fontSize: '14px' }}>Remove</span>
                            </span>
                        )}
                    </div>
                );
            }
        }
    }), [renderInProgressButtons, renderAbortButton, handleRetryUpload, handleRemoveUpload, handleUploadedDatasetClick]);

    // Show "Add data" menu only when completed datasets exist
    const menuConfig = useMemo(() => {
        const hasCompletedDatasets = dataList.some(item => !item.isActive);
        
        if (!hasCompletedDatasets) {
            return undefined;
        }
        
        return {
            rowMenuType: "menu",
            rowOptions: [
                {
                    value: "add_data",
                    label: "Add data",
                }
            ]
        };
    }, [dataList.length]); // Fixed dependency

    
    const dataGridKey = useMemo(() => {
        const contractId = selectedContract?.ContractID || 'no-contract';
        return `datasets-grid-${contractId}-${pagination.currentPage}-${dataList.length}`;
    }, [selectedContract?.ContractID, pagination.currentPage, dataList.length]);
    const buildEditingDataFromResult = (result) => ({
        datasetId: result.DatasetID,
        datasetName: result.DatasetName,
        datasetDisplayName: result.DatasetDisplayName,
        contractId: result.ContractID,
        contractName: result.ContractName,
        contractType: result.ContractType,
        businessUnit: result.BusinessUnit,
        project: result.Project,
        tags: result.CustomTags || [],
        DataProviderID: result.DataProviderID,
        DataPartnerName: result.DataPartnerName,
        DataPartnerDisplayName: result.DataPartnerDisplayName,
        BusinessUnit: result.BusinessUnit,
        Project: result.Project,
        ContractID: result.ContractID,
        CustomTags: result.CustomTags || []
    });

    const handleAddData = useCallback(async (row) => {
        if (row.originalData && row.originalData.Status === DATASET_STATUS.UPLOAD_ABORTED) {
            dispatch(addErrorNotification({
                message: `Cannot add data to aborted dataset "${row.originalData.DatasetName}". Please create a new dataset instead.`
            }));
            return;
        }
        if (row.originalData && row.originalData.Status === DATASET_STATUS.UPLOAD_COMPLETED) {
            dispatch(addErrorNotification({
                message: `Cannot add data to completed dataset "${row.originalData.DatasetName}". The dataset is already finalized.`
            }));
            return;
        }

        if (row && row.originalData) {
            const datasetId = row.originalData.DatasetID;
            logger.debug(`Fetching full dataset details for "${row.originalData.DatasetName}" (ID: ${datasetId})`);
            try {
                const result = await dispatch(fetchDatasetById(datasetId)).unwrap();
                logger.debug('Full dataset details:', result);

                setEditingData(buildEditingDataFromResult(result));
                setIsAddingData(true);
                setIsCreateDataUpload(true);
            } catch (error) {
                console.error('Failed to fetch dataset details:', error);
                const dataset = row.originalData;
                setEditingData(buildEditingDataFromResult(dataset));
                setIsAddingData(true);
                setIsCreateDataUpload(true);
            }
        }
    }, [dispatch]);

    const handleMenuSelect = useCallback(async (event) => {
        const { detail } = event;
        if (!detail) return;

        const row = detail.payload;
        if (!row) return;

         if (row.isActive) {
            dispatch(addErrorNotification({
                message: `Cannot add data while upload is in progress for "${row.name}". Please wait for the upload to complete.`
            }));
            return;
        }

        switch (detail.value) {
            case "add_data":
                await handleAddData(row);
                break;

            case "delete_contract":
                logger.debug(`Delete ${row.name}`);
                break;

            case "edit_contract":
                if (row) {
                    setEditingData(row);
                    setIsCreateDataUpload(true);
                }
                break;

            default:
                break;
        }
    }, [dispatch, handleAddData]);

 
    const handleBreadcrumbNavigation = useCallback((event) => {
        const { detail } = event;
        if (detail.value === 'Contracts') {
            handleBackToContracts();
        }
    }, []);

 
    useEffect(() => {
        window.addEventListener('eds-breadcrumb-click', handleBreadcrumbNavigation);
        return () => window.removeEventListener('eds-breadcrumb-click', handleBreadcrumbNavigation);
    }, [handleBreadcrumbNavigation]);

    useEffect(() => {
        window.addEventListener("eds-datagrid-menu-option-select", handleMenuSelect);
        return () => {
            window.removeEventListener("eds-datagrid-menu-option-select", handleMenuSelect);
        };
    }, [handleMenuSelect]);

    if (selectedDataset) {
        return <DataUploadChild data={selectedDataset} onBack={() => setSelectedDataset(null)} />;
    }

   return (  
    <>     
        {!isCreateDataUpload && (    
            <div className="data-upload-container">
                <div className="upload-data-header fill--1">
                   {currentView === 'datasets' && selectedContract && (
                        <EDSBreadcrumb 
                            options={[
                                { label: 'Contracts', value: 'Contracts', url: '#' },
                                { 
                                    label: selectedContract.ContractDisplayName || selectedContract.Name, 
                                    value: selectedContract.ContractDisplayName || selectedContract.Name, 
                                    url: '#' 
                                }
                            ]} 
                        />
                    )}
                </div>

                {/* ✅ PRESERVED: Data Summary Cards (commented but kept for future use) */}
                {/* <div className='data-summary-cards'>
                    {summaryData.map((data, index) => (
                        <DataSummaryCard 
                            key={index}
                            title={data.title}
                            value={data.value}
                            progress={data.progress}
                            color={data.color}
                        />
                    ))}
                </div> */}

                <div className="projects-container fill--1">
                    {/* ✅ CONTRACTS VIEW */}
                  {currentView === 'contracts' && (
                            <>
                                {isMounting ? (
                                    <SpinnerComponent 
                                        isVisible={true}
                                        overlay={false}
                                        message="Loading contracts..."
                                    />
                                ) : (
                                    <ContractsList
                                        contracts={contracts}
                                        selectedContract={selectedContract}
                                        onSelectContract={handleContractSelect}
                                        pagination={contractsPagination}
                                        onPreviousPage={handleContractsPreviousPage}
                                        onNextPage={handleContractsNextPage}
                                        onPageNumberClick={handleContractsPageNumberClick}
                                        isLoading={isContractsLoading}
                                        searchTerm={contractSearchTerm}
                                        onSearchInput={handleContractSearchInput}
                                        onSearchClear={handleContractSearchClear}
                                    />
                                )}
                            </>
                        )}

                    {/*  DATASETS VIEW */}
                    {currentView === 'datasets' && selectedContract && (
                        <>
                            {isMounting ? (
                                <SpinnerComponent 
                                    isVisible={true}
                                    overlay={false}
                                    message="Loading datasets..."
                                />
                            ) : (
                                <>
                                    {/* Always show upload section when in datasets view */}
                                   <div className='upload-data-section'>
                                        <h4 className='u-mt0 u-mb0'>Uploaded data</h4>
                                        <div className='flex'>
                                            <span className='u-mr'>
                                                <EDSButton
                                                    variant={"primary"}
                                                    type={"button"}
                                                    label={fileExtensionsLoading ? "Loading..." : "Upload data"}
                                                    icon={{"name":"ico-upload-16","position":"left"}}                               
                                                    onEDSButtonClick={createUploadButtonClick}
                                                    isDisabled={!isUploadEnabled || fileExtensionsLoading}
                                                />
                                            </span>
                                            <span style={{'width':'350px'}}>
                                                <EDSSearch
                                                    useSearchButton={false}
                                                    variant={"type-in"}
                                                    addListOption={"beginning"}
                                                    placeholder={"Search datasets..."}
                                                    onEDSSearchInput={handleSearchInput}
                                                    onEDSSearchClear={handleSearchClear}
          
                                                />
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Always show grid - let EDS handle empty states */}
                                    <div>
                                        <EDSDataGrid
                                            key={dataGridKey}
                                            activeRowId={0}
                                            columnConfig={dataColumn}
                                            columnFreezing={""}
                                            menuConfig={menuConfig}
                                            disabledRowCells={{}}
                                            gridFeatures={["row-hover","table-flex-horizontal"]}
                                            isLoading={isLoading || isPaginationLoading}
                                            loaderType={"activity-indicator-linear"}
                                            labels={{
                                                "rowText":"rows of",
                                                "multiSelectText":"{{rowCount}} item(s) are selected",
                                                "rowsPerPageText":"Rows per page",
                                                "noRowsFoundText":"No datasets found",
                                                "noFilterResultsText": searchTerm ? `No results found for "${searchTerm}"` : "No datasets found",
                                                "emptyGridMessage": "No uploads available for this contract.",   
                                                "rowsSelected":"row(s) selected",
                                                "rowAddPlaceholderText":"Add text",
                                                "rowEditPlaceholderText":"Edit row",
                                                "rowAddModalHeaderText":"Add row",
                                                "rowEditModalHeaderText":"Edit row",
                                                "rowDeleteModalHeaderText":"Delete row",
                                                "rowAddModalAcceptButtonText":"Add row",
                                                "rowAddModalCancelButtonText":"Cancel",
                                                "loadMore":"Load more",
                                                "rowEditModalAcceptButtonText":"Save",
                                                "rowEditModalCancelButtonText":"Cancel",
                                                "rowDeleteModalAcceptButtonText":"Delete",
                                                "rowDeleteModalCancelButtonText":"Cancel"
                                            }}
                                            loadedRowIds={[]}
                                            rowFormat={{"size":"standard","style":"divider"}}
                                            rows={filteredDataList}
                                            visibleRowCount={Math.min(10, filteredDataList.length)}
                                            totalRowCount={filteredDataList.length}
                                            modalWidth={"382px"}
                                            sortType={""}
                                            sort={[]}
                                            utilityButtonVariant={"tertiary"}
                                        />

                                       {/* EDS Pagination — shows when total > pageSize */}
                                        {pagination.total > 0 && pagination.total > pagination.pageSize && (
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '12px 0',
                                                marginTop: '8px'
                                            }}>
                                                <div style={{ fontSize: '13px', color: '#666' }}>
                                                    {(() => {
                                                        const start = (pagination.currentPage - 1) * pagination.pageSize + 1;
                                                        const end = Math.min(
                                                            pagination.currentPage * pagination.pageSize,
                                                            pagination.total
                                                        );
                                                        return `${start}-${end} of ${pagination.total} datasets`;
                                                    })()}
                                                </div>
                                                <EDSPagination
                                                    totalRecords={pagination.total}
                                                    pageSizeOptions={{
                                                        value: pagination.pageSize,
                                                        options: [],
                                                        label: ""
                                                    }}
                                                    currentPage={pagination.currentPage}
                                                    onEDSPaginationPageNumberClick={handleDatasetsPageNumberClick}
                                                    onEDSPaginationPageNextClick={handleNextPage}
                                                    onEDSPaginationPagePreviousClick={handlePreviousPage}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        )}
        
        {/* Upload modal */}
        {isCreateDataUpload && 
            <CreateDataUpload
                onClose={handleCloseCreateUpload}
                createDataUpload={handleCreateDataUpload}
                editingData={editingData}
                isAddingData={isAddingData}
                selectedContract={selectedContract}
            />
        }
          <UploadSummaryModal
            summary={completedUploadSummary}
            onClose={handleCloseSummaryModal}
        />
    </>
);
};

export default DataUpload;
     